import { supabase } from '@/lib/supabase';

export type StudentListItem = {
  class_id: string;
  class_label: string;
  created_at: string;
  email: string;
  full_name: string | null;
  id: string;
  is_active: boolean;
  profile_id: string;
  roll_number: string;
};

const CREATE_STUDENT_FUNCTION = 'admin-create-student';
const FETCH_CLASS_STUDENTS_FUNCTION = 'fetch-class-students';
const UPDATE_STUDENT_STATUS_FUNCTION = 'admin-update-student-status';

function normalizeStudentName(fullName: string | null | undefined, rollNumber: string) {
  const trimmedName = fullName?.trim();
  return trimmedName && trimmedName.length > 0 ? trimmedName : `Student ${rollNumber}`;
}

export async function fetchStudentsForSchool(schoolId: string) {
  const [{ data: students, error: studentsError }, { data: profiles, error: profilesError }, { data: classes, error: classesError }] =
    await Promise.all([
      supabase
        .from('students')
        .select('id, profile_id, class_id, roll_number, created_at')
        .eq('school_id', schoolId)
        .order('created_at', { ascending: false }),
      supabase
        .from('profiles')
        .select('id, email, full_name, is_active')
        .eq('school_id', schoolId)
        .eq('role', 'student'),
      supabase
        .from('classes')
        .select('id, name, section')
        .eq('school_id', schoolId),
    ]);

  if (studentsError) throw studentsError;
  if (profilesError) throw profilesError;
  if (classesError) throw classesError;

  const profilesMap = new Map(
    (profiles ?? []).map((profile) => [profile.id, profile] as const)
  );
  const classesMap = new Map(
    (classes ?? []).map((item) => [
      item.id,
      item.section?.trim() ? `${item.name} - ${item.section}` : item.name,
    ] as const)
  );

  return (students ?? [])
    .map((student) => {
      const profile = profilesMap.get(student.profile_id);
      if (!profile) return null;

      return {
        ...student,
        class_label: classesMap.get(student.class_id) ?? 'Class unavailable',
        email: profile.email,
        full_name: normalizeStudentName(profile.full_name, student.roll_number),
        is_active: profile.is_active,
      };
    })
    .filter(Boolean) as StudentListItem[];
}

export async function fetchStudentsForClass(classId: string, schoolId?: string) {
  const { data, error } = await supabase.functions.invoke(FETCH_CLASS_STUDENTS_FUNCTION, {
    body: {
      classId,
      schoolId,
    },
  });

  if (error) throw error;

  return ((data?.students as StudentListItem[] | undefined) ?? []).map((student) => ({
    ...student,
    full_name: normalizeStudentName(student.full_name, student.roll_number),
  }));
}

export async function createStudentAccount(input: {
  classId: string;
  email: string;
  fullName: string;
  password: string;
  rollNumber: string;
  schoolId: string;
}) {
  const { data, error } = await supabase.functions.invoke(CREATE_STUDENT_FUNCTION, {
    body: {
      classId: input.classId,
      email: input.email.trim(),
      fullName: input.fullName.trim(),
      password: input.password,
      role: 'student',
      rollNumber: input.rollNumber.trim(),
      schoolId: input.schoolId,
    },
  });

  if (error) throw error;

  return data;
}

export async function updateStudentProfile(input: {
  classId: string;
  fullName: string;
  profileId: string;
  rollNumber: string;
  schoolId: string;
  studentId: string;
}) {
  const [profileResult, studentResult] = await Promise.all([
    supabase
      .from('profiles')
      .update({
        full_name: input.fullName.trim(),
      })
      .eq('id', input.profileId)
      .eq('school_id', input.schoolId)
      .eq('role', 'student'),
    supabase
      .from('students')
      .update({
        class_id: input.classId,
        roll_number: input.rollNumber.trim(),
      })
      .eq('id', input.studentId)
      .eq('school_id', input.schoolId),
  ]);

  if (profileResult.error) throw profileResult.error;
  if (studentResult.error) throw studentResult.error;
}

export async function toggleStudentActive(input: {
  isActive: boolean;
  profileId: string;
  schoolId: string;
}) {
  const { error } = await supabase.functions.invoke(UPDATE_STUDENT_STATUS_FUNCTION, {
    body: {
      isActive: input.isActive,
      profileId: input.profileId,
      schoolId: input.schoolId,
    },
  });

  if (error) throw error;
}
